<template>
  <div />
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import type {
  AudioAnalyserVolumeCallbackData,
  AudioAnalyserRecordingCallbackData,
  TranscriptionResult,
  WordOrPhraseMatch,
  ManagedTimeout,
  progressSateError
} from '@/customTypes'
import { storeToRefs } from 'pinia'

// Store
import { useDataStore } from '@/stores/DataStore'
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useLiveSpeechStore } from '@/stores/LiveSpeechStore'
import type { LateTranscriptEvent } from '@/stores/LiveSpeechStore'
import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'
import { useInconclusiveTrackerStore } from '@/stores/inconclusiveTrackerStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

import Logger from '@/services/Logger.js'

const logger: any = new Logger('RecordingComponent', true)

type DockableDetail = {
  questionId: number
  subtestId: number
  serverSaysAnswered: boolean
  lateTranscriptReceived: boolean
  source: 'server-confirm' | 'deepgram-late' | 'both'
}

const COMPONENT_ERROR_TYPE: progressSateError = 'NoAnswersDetected'
const NO_ANSWER_WARNING_THRESHOLD = 3 // consecutive unanswered before warning teacher
const RECORDING_TAIL_BUFFER_MS = 200 // mic listens past visible end for slow speakers
const INCONCLUSIVE_SUBTEST_THRESHOLD = 6 // flag-only threshold for subtest summary log
const SUBTEST_SUMMARY_DELAY_MS = 4000 // gives phoneme model time to land on final question

const dataStore = useDataStore()
const { currentQuestion, currentSubtest } = storeToRefs(dataStore)

const originalQuestionDeadlineMs: Ref<number | null> = ref(null)
const appliedMultiplier: Ref<number> = ref(1)
const MAX_EXTENSION_MULTIPLIER = 3

function readMultiplier(questionId: number): number {
  const map = activeScreenerStore.activeScreener?.singleQuestionMultipliers
  if (!map) return 1
  const raw = map[questionId] ?? map[String(questionId)]
  return typeof raw === 'number' && raw > 1 ? raw : 1
}

function applyMultiplierChange(nextMultiplier: number): void {
  if (waitTimeElapsed || earlyStopTriggered) return
  const originalDeadline = originalQuestionDeadlineMs.value
  if (!waitingRecordingTimeout.value || originalDeadline === null) return

  const clamped = Math.min(MAX_EXTENSION_MULTIPLIER, Math.max(1, nextMultiplier))
  // Only honour increases — there's no path back down without a cancel button.
  if (clamped <= appliedMultiplier.value) return

  const newDeadline = originalDeadline + (clamped - 1) * props.waitTime * 1000
  const remainingMs = newDeadline - Date.now()
  logger.info(
    `Extending current question to multiplier x${clamped} (${remainingMs}ms remaining)`
  )
  clearManagedTimeout(waitingRecordingTimeout)
  waitingRecordingTimeout.value = setManagedTimeout(() => {
    if (earlyStopTriggered) return
    waitTimeElapsed = true
    clearManagedTimeout(speechTimeout)
    stopRecording(checkForAnswer)
  }, Math.max(0, remainingMs))
  appliedMultiplier.value = clamped
}

const mediaDevicesStore = useMediaDevicesStore()
const soundEffectsStore = useSoundEffectsStore()
const { audioContext } = storeToRefs(soundEffectsStore)

const liveSpeechStore = useLiveSpeechStore()
const audioAnalyserStore = useAudioAnalyserStore()
const activeScreenerStore = useActiveScreenerStore()
const inconclusiveTracker = useInconclusiveTrackerStore()
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

// Kept alive through the next question so late transcripts can still arrive.
const previousQuestionId: Ref<number | null> = ref(null)

const props = defineProps({
  questionId: { type: Number, default: 0 },
  waitTime: { type: Number, default: 0 },
  record: { type: Boolean, default: false },
  waitingForAnswer: { type: Boolean, default: false },
  isPracticeQuestion: { type: Boolean, default: false },
  proceedButton: { type: Function, required: true }
})

const emit = defineEmits<{
  (e: 'recording-finished', answered: boolean): void
  (e: 'play-prompt'): void
}>()

const attemptCount: Ref<number> = ref(1)

const threshold: number = 7
const soundRecorded: Ref<boolean> = ref(false)

const wordsDetectedInAudio: Ref<boolean> = ref(false)
let speechTimeout = ref<ManagedTimeout | null>(null)
let waitingRecordingTimeout = ref<ManagedTimeout | null>(null)
const liveTranscribing: Ref<boolean> = ref(false)
let earlyStopTriggered = false
let waitTimeElapsed = false

watch(
  () => {
    const id = props.questionId
    if (!id) return null
    return readMultiplier(id)
  },
  (next) => {
    if (next === null) return
    applyMultiplierChange(next)
  }
)

const analyserSetup: Ref<boolean> = ref(false)
const noTranscriptionCount: Ref<number> = ref(0)
const hadEmptyRecording = ref(false)
const deepgramFallbackActive: Ref<boolean> = ref(false)
const deepgramWarningSent: Ref<boolean> = ref(false)

const noAnswersDetectedSubtestId: Ref<number | null> = ref(null)
const noAnswersDetectedCount: Ref<number> = ref(0)
const deepgramTranscriptionLog: Ref<string> = ref('')
const transcriptionSegmentOpen: Ref<boolean> = ref(false)
const soundRecordedSamples: Ref<number[]> = ref([])
const MAX_SOUND_RECORDED_SAMPLES = 25

const recordingStreamReady: ComputedRef<boolean> = computed(() => {
  return !!(mediaDevicesStore.recordingStream && soundEffectsStore.audioContext)
})

const wordOrAudioDetected = computed(() => {
  return liveTranscribing.value ? wordsDetectedInAudio.value : soundRecorded.value
})

const screenerHasComponentError = computed(() =>
  activeScreenerStore.hasErrorState(COMPONENT_ERROR_TYPE)
)

onMounted(() => {
  const stopWatching = watch(recordingStreamReady, (stream) => {
    if (stream) {
      logger.info('Recording Stream Ready setup analyser')
      analyserSetup.value = true
      setupAudioAnalyser()
      stopWatching()
    }
  })

  if (recordingStreamReady.value) {
    logger.info('Recording Stream already Ready setup analyser')
    stopWatching()
    if (!analyserSetup.value) {
      analyserSetup.value = true
      setupAudioAnalyser()
    }
  }
})

onBeforeUnmount(() => {
  stopRecording()
  liveSpeechStore.closeAll()
  audioAnalyserStore.destroyAudioAnalyser()
})

watch(
  () => activeScreenerStore.modelConclusions,
  (newMap) => {
    if (!newMap) return
    for (const [qidStr, val] of Object.entries(newMap)) {
      if (val !== true) continue
      const qid = Number(qidStr)
      const entry = inconclusiveTracker.findLatestByQuestionId(qid)
      if (!entry || entry.subtestUnansweredDocked) continue
      dockSubtestUnansweredFor(qid, entry.subtestId, 'server-confirm')
    }
  }
)

function emitSubtestSummaryLog(subtestId: number, subtestIndex: number) {
  const counts = activeScreenerStore.inconclusiveAnswersCounts
  const screenerUnresponsiveCount = counts.screenerUnresponsive[subtestIndex] ?? 0
  const sttUnresponsiveCount = counts.sttUnresponsive[subtestIndex] ?? 0
  const modelUnresponsiveCount = counts.modelUnresponsive[subtestIndex] ?? 0
  void activeScreenerStore.logToDatabase('inconclusive_subtest_summary', {
    subtestId,
    subtestIndex,
    screenerUnresponsiveCount,
    sttUnresponsiveCount,
    modelUnresponsiveCount,
    threshold: INCONCLUSIVE_SUBTEST_THRESHOLD,
    modelUnresponsiveExceedsThreshold: modelUnresponsiveCount >= INCONCLUSIVE_SUBTEST_THRESHOLD
  })
}

watch(
  () => currentSubtest.value,
  (newSubtest, oldSubtest) => {
    if (!oldSubtest || !newSubtest) return
    if (newSubtest.id === oldSubtest.id) return
    if (typeof oldSubtest.index !== 'number') return
    const priorSubtestId = oldSubtest.id
    const priorSubtestIndex = oldSubtest.index
    setManagedTimeout(
      () => emitSubtestSummaryLog(priorSubtestId, priorSubtestIndex),
      SUBTEST_SUMMARY_DELAY_MS
    )
  }
)

// No subtest transition after the last one — emit its summary on Completed/Finishing.
watch(
  () => activeScreenerStore.activeScreener?.state,
  (newState) => {
    if (newState !== 'Completed' && newState !== 'Finishing') return
    const subtest = currentSubtest.value
    if (!subtest || typeof subtest.index !== 'number') return
    const subtestId = subtest.id
    const subtestIndex = subtest.index
    setManagedTimeout(
      () => emitSubtestSummaryLog(subtestId, subtestIndex),
      SUBTEST_SUMMARY_DELAY_MS
    )
  }
)

watch(
  () => props.waitingForAnswer,
  (newVal) => {
    if (newVal === false || !props.record) return
    if (!recordingStreamReady.value) {
      logger.error(
        'Failed to wait for answer. No Stream exists:',
        mediaDevicesStore.recordingStream
      )
    }
    soundRecorded.value = false
    wordsDetectedInAudio.value = false
    waitForAudioAnswer()
  }
)

watch(
  () => props.questionId,
  (newId, oldId) => {
    if (newId === oldId) return
    waitTimeElapsed = false
    earlyStopTriggered = false
    appliedMultiplier.value = 1
  }
)

function initiateRecording(): void {
  startRecording()
}

const formatErrorForLog = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    }
  }

  if (typeof error === 'string') {
    return { message: error }
  }

  if (error && typeof error === 'object') {
    try {
      return JSON.parse(JSON.stringify(error))
    } catch {
      return { message: String(error) }
    }
  }

  return { message: String(error ?? 'Unknown error') }
}

const logDeepgramEvent = async (event: string, details: Record<string, any> = {}) => {
  try {
    await activeScreenerStore.logToDatabase(event, {
      questionId: props.questionId,
      subtestKey: currentSubtest.value?.key ?? null,
      fallbackActive: deepgramFallbackActive.value,
      ...details
    })
  } catch (error) {
    logger.error('Failed to log Deepgram event', { event, error })
  }
}

const activateDeepgramFallback = (reason: string, extra: Record<string, any> = {}) => {
  if (deepgramFallbackActive.value) return
  deepgramFallbackActive.value = true
  liveTranscribing.value = false
  logger.warn('Activating Deepgram fallback', { reason, extra })
  void logDeepgramEvent('deepgram_fallback_activated', { reason, ...extra })
}

const handleDeepgramRuntimeError = (error: unknown) => {
  logger.error('Deepgram runtime error', error)
  const errorDetails = formatErrorForLog(error)
  void logDeepgramEvent('deepgram_error', { error: errorDetails })
  activateDeepgramFallback('runtime_error', { error: errorDetails })
  liveSpeechStore.flushAndClose(props.questionId)
  void notifyTeacherOfDeepgramIssue('runtime_error', errorDetails)
}

const handleDeepgramSetupFailure = (error: unknown) => {
  logger.error('Failed to start Deepgram transcription', error)
  const errorDetails = formatErrorForLog(error)
  liveTranscribing.value = false
  void logDeepgramEvent('deepgram_setup_failed', { error: errorDetails })
  activateDeepgramFallback('setup_failed', { error: errorDetails })
  void notifyTeacherOfDeepgramIssue('setup_failed', errorDetails)
}

const appendToTranscriptionLog = (value: string) => {
  deepgramTranscriptionLog.value = `${deepgramTranscriptionLog.value}${value}`
}

const openTranscriptionSegment = () => {
  if (transcriptionSegmentOpen.value) return
  appendToTranscriptionLog('(')
  soundRecordedSamples.value = []
  transcriptionSegmentOpen.value = true
}

const closeTranscriptionSegment = () => {
  if (!transcriptionSegmentOpen.value) return
  appendToTranscriptionLog('),')
  transcriptionSegmentOpen.value = false
  appendSoundRecordedSegment()
}

const appendTranscriptionText = (text: string) => {
  const cleaned = text?.trim()
  if (!cleaned) return
  if (!transcriptionSegmentOpen.value) {
    openTranscriptionSegment()
  }
  appendToTranscriptionLog(`${cleaned} `)
}

const appendSoundRecordedSegment = () => {
  const samples = soundRecordedSamples.value
  const payload = samples.length > 0 ? samples.map((sample) => sample.toFixed(1)).join(',') : 'none'
  appendToTranscriptionLog(`(Sound levels: ${payload}),`)
  soundRecordedSamples.value = []
}

const recordSoundSample = (value: number) => {
  const rounded = Math.round(value * 10) / 10
  soundRecordedSamples.value.push(rounded)
  if (soundRecordedSamples.value.length > MAX_SOUND_RECORDED_SAMPLES) {
    soundRecordedSamples.value.shift()
  }
}

const notifyTeacherOfDeepgramIssue = async (
  reason: string,
  errorDetails: Record<string, any> | null
) => {
  if (deepgramWarningSent.value) return
  deepgramWarningSent.value = true
  await activeScreenerStore.sendScreenerWarning({
    type: 'DeepgramUnavailable',
    message:
      'Live transcription is unavailable. This can lead to more pausing issues and failure to acknowledge a pupils responses. Please look in the screener guide to make sure the firewall is setup for Deepgram.',
    priority: 1,
    metadata: {
      reason,
      errorDetails
    }
  })
}

const warnOnNonPreferredMicrophoneUse = async () => {
  if (!mediaDevicesStore.hasPreferredInputDeviceAvailable()) return
  if (mediaDevicesStore.isPreferredInputDeviceSelected()) return

  const selectedDevice = mediaDevicesStore.getCurrentDeviceInfo('input')
  const preferredDevices = mediaDevicesStore.getPreferredInputDeviceSummaries()

  await activeScreenerStore.sendScreenerWarning({
    type: 'IncorrectMicInUse',
    message:
      'The recording isn’t using the available recommended headset, this will affect audio quality and may lead to inconclusive reports. Please exit and re-enter the tech test',
    priority: 1,
    metadata: {
      selectedDevice,
      preferredDevices
    }
  })
}

const resolveIncorrectMicInUseWarning = async () => {
  if (
    activeScreenerStore.hasWarningState('IncorrectMicInUse') &&
    mediaDevicesStore.isPreferredInputDeviceSelected()
  ) {
    await activeScreenerStore.sendScreenerResolvedWarning({
      type: 'IncorrectMicInUse'
    })
  }
}

const resolveDeepgramUnavailableWarning = async () => {
  if (activeScreenerStore.hasWarningState('DeepgramUnavailable')) {
    await activeScreenerStore.sendScreenerResolvedWarning({
      type: 'DeepgramUnavailable'
    })
  }
}

function dockSubtestUnansweredFor(questionId: number, subtestId: number, source: string): void {
  const newlyDocked = inconclusiveTracker.markSubtestUnansweredDocked(questionId)
  if (!newlyDocked) return
  if (currentSubtest.value?.id !== subtestId) return
  if (noAnswersDetectedCount.value <= 0) return
  noAnswersDetectedCount.value = Math.max(0, noAnswersDetectedCount.value - 1)
  logger.info('[Deepgram] subtest unanswered count adjusted', {
    questionId,
    subtestId,
    source,
    remainingSubtestUnansweredCount: noAnswersDetectedCount.value
  })
}

function handleLateTranscript({ questionId, subtestId, result }: LateTranscriptEvent): void {
  const text = result?.text?.trim()
  logger.info('[Deepgram] late transcript received — flagged for fire-time check', {
    questionId,
    subtestId,
    text: text ?? '',
    matched: result?.matched ?? false
  })
  if (!text) return
  inconclusiveTracker.markLateTranscript(questionId)
  void activeScreenerStore.logToDatabase('question_answer_found_late_live_stt', {
    questionId,
    subtestId,
    subtestKey: currentSubtest.value?.key ?? null,
    transcriptText: text,
    matched: result?.matched ?? false
  })
  if (subtestId !== null) {
    dockSubtestUnansweredFor(questionId, subtestId, 'late-live-stt')
  }
}

function waitForAudioAnswer(): void {
  earlyStopTriggered = false
  waitTimeElapsed = false
  deepgramFallbackActive.value = false
  deepgramTranscriptionLog.value = ''
  transcriptionSegmentOpen.value = false

  if (!audioAnalyserStore.isRecording) {
    startRecording()
  } else {
    openTranscriptionSegment()
  }
  if (currentQuestion.value) {
    const deepgramTargets: WordOrPhraseMatch | undefined = liveSpeechStore.convertTargetFormat(
      currentQuestion.value,
      currentSubtest.value?.key
    )
    if (deepgramTargets) {
      liveSpeechStore.setItemsToMatch(deepgramTargets)
    }
  }

  logger.info('Waiting for audio answer', props.waitTime)
  liveTranscribing.value = true

  const onTranscript = (result: TranscriptionResult) => {
    if (!result.text) return
    if (noTranscriptionCount.value >= 1 || screenerHasComponentError.value) {
      sendResponsiveNotification()
    }
    logger.info('Transcription result', result.text)
    appendTranscriptionText(result.text)
    noTranscriptionCount.value = 0
    wordsDetectedInAudio.value = true
    resolveDeepgramUnavailableWarning()
    if (waitTimeElapsed) return
    if (result.matched && !earlyStopTriggered) {
      liveSpeechStore.markAnsweredLive(props.questionId)
      skipWaitingForAnswer()
    }
    clearManagedTimeout(speechTimeout)

    speechTimeout.value = setManagedTimeout(() => {
      if (!earlyStopTriggered && currentSubtest.value?.metadata?.buttonProgress) {
        props.proceedButton()
      }
    }, 1000)
  }

  liveSpeechStore
    .startTranscription({
      questionId: props.questionId,
      subtestId: currentSubtest.value?.id ?? null,
      onTranscript,
      onLateTranscript: handleLateTranscript,
      onError: handleDeepgramRuntimeError
    })
    .then(() => {
      // Keep current + immediately-prior connection alive; dispose anything older.
      const keep = new Set<number>([props.questionId])
      if (previousQuestionId.value !== null) keep.add(previousQuestionId.value)
      liveSpeechStore.closeAllExceptIds(keep)
      if (previousQuestionId.value !== props.questionId) {
        previousQuestionId.value = props.questionId
      }
    })
    .catch((error: unknown) => {
      handleDeepgramSetupFailure(error)
    })

  originalQuestionDeadlineMs.value = Date.now() + props.waitTime * 1000 + RECORDING_TAIL_BUFFER_MS
  const startingMultiplier = Math.min(
    MAX_EXTENSION_MULTIPLIER,
    Math.max(1, readMultiplier(props.questionId))
  )
  appliedMultiplier.value = startingMultiplier
  activeScreenerStore.updateFirebase({
    originalQuestionDeadline: originalQuestionDeadlineMs.value
  })

  const totalWaitMs = props.waitTime * startingMultiplier * 1000 + RECORDING_TAIL_BUFFER_MS
  if (startingMultiplier > 1) {
    logger.info(
      `Applying queued extension at start of wait window: x${startingMultiplier} (${totalWaitMs}ms)`
    )
  }
  waitingRecordingTimeout.value = setManagedTimeout(() => {
    if (earlyStopTriggered) return
    waitTimeElapsed = true
    logger.info('wait time elapsed')
    clearManagedTimeout(speechTimeout)
    stopRecording(checkForAnswer)
  }, totalWaitMs)
}

function skipWaitingForAnswer() {
  logger.info('Wait time Skipped')
  earlyStopTriggered = true
  clearManagedTimeout(waitingRecordingTimeout)
  clearManagedTimeout(speechTimeout)
  stopRecording(checkForAnswer)
}

function trackSubtestUnanswered() {
  const currentSubtestId = currentSubtest.value?.id ?? null
  if (currentSubtestId !== noAnswersDetectedSubtestId.value) {
    noAnswersDetectedSubtestId.value = currentSubtestId
    noAnswersDetectedCount.value = 1
  } else {
    noAnswersDetectedCount.value++
  }
}

// Prior inconclusives the server or a late Deepgram event has since confirmed answered.
function collectDockableFromPostHocAnswers(): DockableDetail[] {
  const candidates = inconclusiveTracker.getLast(NO_ANSWER_WARNING_THRESHOLD)
  const dockable: DockableDetail[] = []
  for (const entry of candidates) {
    const serverSaysAnswered = activeScreenerStore.modelConclusions[entry.questionId] === true
    const lateTranscriptReceived = entry.lateTranscriptReceived
    if (!serverSaysAnswered && !lateTranscriptReceived) continue
    const source: DockableDetail['source'] =
      serverSaysAnswered && lateTranscriptReceived
        ? 'both'
        : serverSaysAnswered
          ? 'server-confirm'
          : 'deepgram-late'
    dockable.push({
      questionId: entry.questionId,
      subtestId: entry.subtestId,
      serverSaysAnswered,
      lateTranscriptReceived,
      source
    })
    logger.info('[Deepgram] dockable prior question — server or late deepgram confirmed answer', {
      questionId: entry.questionId,
      subtestId: entry.subtestId,
      source
    })
  }
  return dockable
}

// Returns true if the warning fired, false if suppressed by a confirmed prior answer.
async function fireNoAnswersWarning(): Promise<boolean> {
  const dockable = collectDockableFromPostHocAnswers()
  if (dockable.length > 0) {
    noTranscriptionCount.value = Math.max(0, noTranscriptionCount.value - dockable.length)
    if (noTranscriptionCount.value < NO_ANSWER_WARNING_THRESHOLD) {
      logger.info('[Deepgram] unresponsive warning suppressed — server/late deepgram redeemed', {
        dockedFromPostHocAnswers: dockable.length,
        remainingWarningCount: noTranscriptionCount.value
      })
      await activeScreenerStore.logToDatabase('no_answers_warning_suppressed', {
        questionId: props.questionId,
        subtestKey: currentSubtest.value?.key ?? null,
        subtestId: noAnswersDetectedSubtestId.value,
        dockedFromPostHocAnswers: dockable.length,
        dockedQuestions: dockable,
        remainingWarningCount: noTranscriptionCount.value
      })
      return false
    }
  }

  const transcriptionLog = deepgramTranscriptionLog.value || 'No transcription detected'
  await activeScreenerStore.logToDatabase('no_answers_detected', {
    questionId: props.questionId,
    subtestKey: currentSubtest.value?.key ?? null,
    subtestId: noAnswersDetectedSubtestId.value,
    message: 'No answers detected for the last few questions.',
    deepgramFallbackActive: deepgramFallbackActive.value,
    subtestUnansweredCount: noAnswersDetectedCount.value,
    dockedFromPostHocAnswers: dockable.length,
    dockedQuestions: dockable,
    transcriptionLog
  })
  await activeScreenerStore.sendScreenerError({
    type: COMPONENT_ERROR_TYPE,
    message:
      'No answers detected for the last few questions, lack of answers will result in an inconclusive report.',
    priority: 2,
    metadata: { transcriptionLog }
  })
  dataStore.setPausedState(true)
  noTranscriptionCount.value = 0
  return true
}

function startRecording() {
  logger.info('Starting recording')
  openTranscriptionSegment()
  if (!recordingStreamReady.value) {
    logger.error('No recording stream unable to start Recording')
    dataStore.setScreenerError(
      'recordingError',
      'Sorry, there was an error recording the users answer. Please refresh the page and try again. \n If the problem persists, please contact the support team.'
    )
  }
  liveTranscribing.value = false
  audioAnalyserStore.startRecordingSound()
}
/**
 * Check if audio analyzer recorded any sound and if so, emit the recording-finished, else for practice questions, play the prompt.
 */
async function checkForAnswer({ inputBlob, inputDuration }: AudioAnalyserRecordingCallbackData) {
  if (await checkForEmptyRecording(inputBlob)) return

  await activeScreenerStore.logToDatabase('recording_summary', {
    questionId: props.questionId,
    subtestKey: currentSubtest.value?.key ?? null,
    transcriptionLog: deepgramTranscriptionLog.value,
    deepgramFallbackActive: deepgramFallbackActive.value,
    wordOrAudioDetected: wordOrAudioDetected.value,
    inputDuration: inputDuration,
    inputBlobSize: inputBlob.size
  })

  if (wordOrAudioDetected.value) {
    noTranscriptionCount.value = 0
  } else {
    noTranscriptionCount.value++
    trackSubtestUnanswered()

    const subtestIdForTracker = currentSubtest.value?.id ?? null
    const subtestIndexForTracker = currentSubtest.value?.index
    if (subtestIdForTracker !== null) {
      inconclusiveTracker.recordInconclusive({
        questionId: props.questionId,
        subtestId: subtestIdForTracker,
        questionIndex: currentQuestion.value?.index ?? -1
      })
    }
    if (typeof subtestIndexForTracker === 'number') {
      activeScreenerStore.incrementScreenerUnresponsive(subtestIndexForTracker)
    }

    const enforceNoAnswersPause = _ENFORCE_NO_ANSWERS_PAUSE_?.toLowerCase() === 'true'
    if (enforceNoAnswersPause) {
      if (noTranscriptionCount.value >= NO_ANSWER_WARNING_THRESHOLD) {
        const fired = await fireNoAnswersWarning()
        if (fired) return
      }
    }
  }

  if (wordOrAudioDetected.value || !props.isPracticeQuestion || attemptCount.value > 1) {
    logger.info('Answer given')
    emit('recording-finished', wordOrAudioDetected.value)
    dataStore.audioAnswerGiven(
      inputBlob,
      wordOrAudioDetected.value,
      inputDuration,
      wordsDetectedInAudio.value
    )
    await resolveIncorrectMicInUseWarning()
    await warnOnNonPreferredMicrophoneUse()
    reset()
  } else {
    emit('play-prompt')
    attemptCount.value++
  }
}

async function checkForEmptyRecording(inputBlob: Blob): Promise<boolean> {
  if (inputBlob.size === 0) {
    if (hadEmptyRecording.value) {
      dataStore.setScreenerError(
        'AudioRecordingError',
        'The recorded audio is empty. Please try recording again.'
      )

      return true
    }
    hadEmptyRecording.value = true

    // Attempt recovery of recording stream and play a prompt for the question
    await mediaDevicesStore.closeRecordingStream()
    mediaDevicesStore.setAudioRecordingStream().then(() => {
      setupAudioAnalyser()
      // Play random prompt for the question to repeat recording attempt
      emit('play-prompt')
    })
    return true
  }
  hadEmptyRecording.value = false
  return false
}

/**
 * Sets up the audio analysis for the recording stream.
 */
function setupAudioAnalyser() {
  logger.info('Setting up audio analyser')
  audioAnalyserStore.setupAudioAnalyser(
    audioContext.value as AudioContext,
    'audioinput',
    mediaDevicesStore.recordingStream as MediaStream,
    volumeChangeCallback
  )
}

function volumeChangeCallback({ average }: AudioAnalyserVolumeCallbackData) {
  if (average > threshold) {
    soundRecorded.value = true
    recordSoundSample(average)
  }
}

/**
 * Reset all of the recording properties to their initial values.
 * @returns {void}
 */
function reset() {
  soundRecorded.value = false
  attemptCount.value = 1
  deepgramFallbackActive.value = false
}

function stopRecording(callback?: Function) {
  logger.info('Stopping recording')
  closeTranscriptionSegment()
  liveSpeechStore.detachAudioFeed(props.questionId)
  audioAnalyserStore.stopRecordingSound(callback)
  if (originalQuestionDeadlineMs.value !== null) {
    originalQuestionDeadlineMs.value = null
    appliedMultiplier.value = 1
    activeScreenerStore.updateFirebase({
      originalQuestionDeadline: null
    })
  }
}

function sendResponsiveNotification() {
  activeScreenerStore.sendScreenerResolvedError({
    type: COMPONENT_ERROR_TYPE
  })
}

defineExpose({
  skipWaitingForAnswer,
  initiateRecording
})
</script>

<style lang="scss"></style>
